import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { toast } from "sonner";
import { formatNOK } from "@/lib/format";
import { splitByReadiness, formatBlockers, totalToPay } from "@/lib/extras-payment";
import { useSetExtrasPaymentState } from "@/hooks/use-extras-payment";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import type { ProjectExtra } from "@/hooks/use-project-extras";

interface Props {
  projectId: string;
  /**
   * VIB-2092: manage_extras-permissionen, samme port som «Legg til».
   * DB-triggeren avviser uansett en uautorisert endring, men en knapp som
   * garantert feiler skal ikke vises.
   */
  canManage: boolean;
  extras: ProjectExtra[];
  selectedIds: Set<string>;
  /** Tømmer utvalget etter en vellykket melding. */
  onDone: () => void;
}

/**
 * VIB-2092: PL melder de valgte statistene klare for utbetaling.
 *
 * Erik 29.09: handlingen bor på prosjektkortet, oversikten hos DL i Rapporter.
 *
 * Egen komponent framfor inline i StatisterTab, som alt lå på grensen for
 * komponent-størrelse (CLAUDE.md-regel 1). Vises bare når utvalget inneholder
 * rader som ikke alt er meldt klare, så knappen aldri ser ut som en no-op.
 */
export default function ExtrasPaymentBatchButton({
  projectId,
  canManage,
  extras,
  selectedIds,
  onDone,
}: Props) {
  const setPaymentState = useSetExtrasPaymentState();
  const flagOn = useFeatureFlag("feature_extras_payment_flow");

  const selectedDrafts = extras.filter(
    (e) => selectedIds.has(e.id) && e.payment_state === "draft",
  );

  if (!flagOn || !canManage || selectedDrafts.length === 0) return null;

  /**
   * Deler utvalget framfor å avvise hele batchen: melder PL 20 klare og to
   * mangler kontonummer, skal de 18 gå gjennom og de to nevnes ved navn.
   * Alt-eller-ingenting ville tvunget PL til å lete opp den ene raden selv.
   */
  const handleClick = () => {
    const { ready, blocked } = splitByReadiness(selectedDrafts);

    for (const { extra, blockers } of blocked) {
      toast.error(
        `${extra.full_name ?? extra.name ?? "Uten navn"} mangler ${formatBlockers(blockers)}`,
      );
    }
    if (ready.length === 0) return;

    setPaymentState.mutate(
      {
        extraIds: ready.map((e) => e.id),
        state: "ready_for_payment",
        projectId,
        namesById: Object.fromEntries(
          ready.map((e) => [e.id, e.full_name ?? e.name ?? "(uten navn)"]),
        ),
      },
      { onSuccess: onDone },
    );
  };

  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 text-xs"
      onClick={handleClick}
      disabled={setPaymentState.isPending}
    >
      <SrIcon name="check-circle2" className="w-3 h-3 mr-1" />
      Klar for utbetaling ({selectedDrafts.length})
      <span className="ml-1.5 text-muted-foreground">
        {formatNOK(totalToPay(selectedDrafts))}
      </span>
    </Button>
  );
}
